import { FC, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { trackEvent } from "~/utils/analytics";

import { DocumentsDropzone } from "./list/DocumentsDropzone";

type BaseUploadDocumentsDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onDrop: (files: File[]) => void;
};

type CaseUploadDocumentsDialogProps = BaseUploadDocumentsDialogProps & {
  isKnowledge?: false;
  caseId: string;
  organizationId: string;
};

type KnowledgeUploadDocumentsDialogProps = BaseUploadDocumentsDialogProps & {
  isKnowledge: true;
  organizationId?: string;
  caseId?: never;
};

type UploadDocumentsDialogProps =
  | CaseUploadDocumentsDialogProps
  | KnowledgeUploadDocumentsDialogProps;

export const UploadDocumentsDialog: FC<UploadDocumentsDialogProps> = ({
  onDrop,
  isOpen,
  setIsOpen,
  ...contextProps
}) => {
  const isKnowledge = contextProps.isKnowledge ?? false;
  const caseId = "caseId" in contextProps ? contextProps.caseId : undefined;
  const organizationId =
    "organizationId" in contextProps ? contextProps.organizationId : undefined;

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        eventName: "app/case/upload_dialog.modal/open",
        properties: {
          ...(caseId && { caseId }),
          ...(organizationId && { organizationId }),
          isKnowledge,
        },
      });
    } else {
      trackEvent({
        eventName: "app/case/upload_dialog.modal/close",
        properties: {
          ...(caseId && { caseId }),
          ...(organizationId && { organizationId }),
        },
      });
    }
  }, [isOpen, caseId, organizationId, isKnowledge]);

  const handleDrop = (files: File[]) => {
    onDrop(files);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="documentsList.uploadDocumentsDialog.title"
              defaultMessage={"Upload Documents"}
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="documentsList.uploadDocumentsDialog.description"
              defaultMessage="Select PDF or DOCX files to upload to your case."
            />
          </DialogDescription>
        </DialogHeader>
        <DocumentsDropzone
          onDrop={handleDrop}
          className="h-72"
          isKnowledge={isKnowledge}
        />
      </DialogContent>
    </Dialog>
  );
};
